import clsx from "clsx";
import React, { useCallback, useMemo } from "react";

import * as NavigationUtils from "../../../../lib/util/NavigationUtils";
import * as FleetSelectors from "../../../../store/Fleet/Fleet.selector";
import * as UserSelectors from "../../../../store/User/User.selector";
import * as GroupUtils from "../../../../lib/util/GroupUtils";
import * as AuthUtils from "../../../../lib/util/AuthUtils";
import * as Utils from "../../../../lib/util/Utils";
import * as Api from "../../../../Api";

import Button from "../../Button/Button";
import Text from "../../Text/Text";
import DebugPanelScopeSelect from "./DebugPanelScopeSelect";
import Select, { SelectOption } from "../../../form/Select/Select";
import { useSelector } from "../../../../lib/hook/ReduxHooks";
import { IconError } from "../../../../assets";
import { AuthLevel } from "../../../../lib/consts/AuthConstants";
import { useStorageState } from "../../../../lib/hook/MiscHooks";
import {
	EMULATED_FLEET_ID_KEY,
	EMULATED_GROUP_ID_KEY,
	EMULATED_USER_AUTH_LEVEL_KEY,
} from "../../../../StorageKeys";

const DebugUserLevelEmulation = () => {
	const [emulatedUserLevel, setEmulatedUserLevel] = useStorageState(
		EMULATED_USER_AUTH_LEVEL_KEY,
	);

	const handleResetEmulatedUserLevel = useCallback(() => {
		AuthUtils.resetEmulatedUserLevel();
		Utils.nextFrame(() => {
			Api.API_DEF.clearCache();
			NavigationUtils.refresh();
		});
	}, []);

	const visible = Boolean(emulatedUserLevel);

	const { fleets, groups, scope } = useSelector((state: any) => ({
		fleets: FleetSelectors.getAllFleets(state),
		groups: FleetSelectors.selectCurrentScopeFleetGroups(state),
		scope: UserSelectors.selectCurrentScope(state),
	}));

	const { fleetId: scopeFleetId, group: scopeGroupId } = scope;

	const { groupsLoading, groupList } = useMemo(() => {
		const groupsLoading = groups.pending;
		const groupList: any[] = GroupUtils.flattenGroups(groups.value || []);
		groupList.unshift({ id: null, name: "none selected" } as any);
		groupList.unshift({ id: undefined, name: "Viewed Group" } as any);
		return { groupsLoading, groupList };
	}, [groups]);

	const fleetList = useMemo(() => {
		const fleetList = [...(fleets.value || [])];
		fleetList.unshift({ id: undefined, name: "Viewed Fleet" } as any);
		return fleetList;
	}, [fleets.value]);

	const [emulatedFleetId, _setEmulatedFleetId] = useStorageState(
		EMULATED_FLEET_ID_KEY,
	);
	const [emulatedGroupId, _setEmulatedGroupId] = useStorageState(
		EMULATED_GROUP_ID_KEY,
	);

	const setEmulatedFleetId = useCallback(
		(value: string) => {
			_setEmulatedFleetId(value);
			AuthUtils.setEmulatedFleetId(value);
			Api.API_DEF.clearCache();
			NavigationUtils.refresh();
		},
		[_setEmulatedFleetId],
	);
	const setEmulatedGroupId = useCallback(
		(value: string) => {
			_setEmulatedGroupId(value);
			AuthUtils.setEmulatedGroupId(value);
			Api.API_DEF.clearCache();
			NavigationUtils.refresh();
		},
		[_setEmulatedGroupId],
	);

	const handleFleetChange = useCallback(
		(option: SelectOption | undefined) => {
			setEmulatedFleetId(option?.value);
		},
		[setEmulatedFleetId],
	);

	const handleGroupChange = useCallback(
		(option: SelectOption | undefined) => {
			setEmulatedGroupId(option?.value);
		},
		[setEmulatedGroupId],
	);

	const viewingPermissionDenied = useMemo(() => {
		if (emulatedFleetId && scopeFleetId !== emulatedFleetId) {
			return "different fleet";
		}

		if (
			emulatedGroupId === undefined ||
			(emulatedUserLevel && emulatedUserLevel >= AuthLevel.ADMIN)
		) {
			return false;
		}

		if (
			groups.resolved &&
			!GroupUtils.findGroupAndSubgroupList(groups.value, emulatedGroupId).find(
				(group) => group.id === scopeGroupId,
			)
		) {
			return "not in group";
		}

		return false;
	}, [
		scopeFleetId,
		emulatedFleetId,
		scopeGroupId,
		groups.value,
		groups.resolved,
		emulatedGroupId,
		emulatedUserLevel,
	]);

	return (
		<div
			className={clsx("DebugUserLevelEmulation", {
				"--visible": visible,
			})}
		>
			<b>
				Emulating User Level: {AuthUtils.getAuthLevelName(emulatedUserLevel)}
			</b>
			<p>Select below the fleet and group you are emulating being a part of</p>
			<div className={"--row"}>
				<Select
					className={"DebugPanel__select"}
					fixed={true}
					dev={true}
					label={"Fleet"}
					compact={true}
					onChange={handleFleetChange}
					loading={!fleets.resolved && !fleets.rejected}
					undefinedIsValue={true}
					value={emulatedFleetId}
					required={true}
					options={fleetList.map((fleet) => {
						const optionFleet =
							fleet.id !== undefined
								? fleet
								: fleetList.find((f) => f.id === scopeFleetId);
						return {
							value: fleet.id,
							className: optionFleet?.archived ? "--col-important-steel" : "",
							label: `${
								fleet.id === undefined ? "Viewed Fleet" : optionFleet?.name
							} [${optionFleet?.id || emulatedFleetId}]`,
						};
					})}
				/>
				<Select
					style={{ marginLeft: "5px" }}
					className={"DebugPanel__select"}
					fixed={true}
					dev={true}
					label={"Group"}
					compact={true}
					undefinedIsValue={true}
					onChange={handleGroupChange}
					value={emulatedGroupId}
					loading={groupsLoading}
					options={groupList.map((group) => {
						const optionGroup =
							group.id !== undefined
								? group
								: groupList.find((g) => g.id === scopeGroupId);
						return {
							value: group.id,
							label: `${
								group.id === undefined ? "Viewed Group" : optionGroup?.name
							} [${optionGroup?.id || "__all"}]`,
						};
					})}
				/>
			</div>
			<br />
			<Button dev={true} onClick={handleResetEmulatedUserLevel}>
				Cancel
			</Button>
			<br />
			<hr />
			<div>
				<b>Viewing:</b>
				<p>Select the fleet and group you are viewing as this emulated user</p>
				<DebugPanelScopeSelect />
				{viewingPermissionDenied && (
					<Text
						className={"--color-warning"}
						style={{ padding: 5, paddingTop: 8, display: "block" }}
					>
						<IconError className={"Icon"} /> You are viewing a group that your
						emulated permission doesn't have access to:{" "}
						<b>{viewingPermissionDenied}</b>. Your portal experience will
						reflect this.
					</Text>
				)}
			</div>
		</div>
	);
};

export default DebugUserLevelEmulation;
